/**
 * Embed — chrome-free single-panel view for iframe embedding.
 *
 * URL pattern: /embed/:projectId/:view
 * Supported views: dag, plot, ref-quantities, diff, run-table
 *
 * Query params:
 *   - branch   — initial branch (defaults to "main")
 *   - theme    — "light" | "dark"
 *   - focusNode — node path to auto-select
 *   - plotId   — saved plot to load
 *   - interactive — "true" to allow user interaction (default "true")
 *   - token    — Bearer token for API auth
 */

import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Text, Theme } from "@radix-ui/themes";

import { ProvenanceProvider } from "../context/ProvenanceContext";
import { PlotDataProvider } from "../context/PlotDataContext";
import { ViewStateProvider } from "../context/ViewStateContext";
import { useTheme } from "../context/ThemeContext";
import { initEmbedToken, clearEmbedToken } from "../api/embedAuth";
import { useEmbedMessaging } from "../hooks/useEmbedMessaging";

// -- Panel components (lazy-ish — but keep it simple for v1) --
import DAGPanel from "../components/panels/DAGPanel";
import PlotBuilderPanel from "../components/panels/PlotBuilderPanel";
import ConfigurationPanel from "../components/panels/ConfigurationPanel";
import { DiffView } from "../components/versioning/DiffView";
import DataExplorerPanel from "../components/panels/DataExplorerPanel";

// ---------------------------------------------------------------------------
// View → Component mapping
// ---------------------------------------------------------------------------

type EmbedView = "dag" | "plot" | "ref-quantities" | "diff" | "run-table";

const VALID_VIEWS = new Set<string>(["dag", "plot", "ref-quantities", "diff", "run-table"]);

function PanelContent({ view, projectId }: { view: EmbedView; projectId: string }) {
  switch (view) {
    case "dag":
      return <DAGPanel projectId={projectId} />;
    case "plot":
      return <PlotBuilderPanel projectId={projectId} />;
    case "ref-quantities":
      return <ConfigurationPanel projectId={projectId} />;
    case "diff":
      return <DiffView />;
    case "run-table":
      return <DataExplorerPanel projectId={projectId} />;
    default:
      return (
        <Box p="6" style={{ textAlign: "center" }}>
          <Text color="red">Unknown view: {view}</Text>
        </Box>
      );
  }
}

// ---------------------------------------------------------------------------
// Embed shell (inside providers)
// ---------------------------------------------------------------------------

function EmbedShell({ projectId, view }: { projectId: string; view: EmbedView }) {
  const { sendEvent } = useEmbedMessaging({
    onSetTheme: (_theme) => {
      // ThemeContext toggle is app-wide; for embed we could extend ThemeContext
      // to accept an explicit setter. For v1, we just acknowledge the command.
      // Future: expose setTheme on ThemeContext.
    },
    onReset: () => {
      window.location.reload();
    },
  });

  // Signal readiness to the parent window once mounted.
  useEffect(() => {
    sendEvent({ name: "ready" });
  }, [sendEvent]);

  return (
    <Box style={{ width: "100vw", height: "100vh", overflow: "hidden" }}>
      <PanelContent view={view} projectId={projectId} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Embed() {
  const { projectId, view } = useParams<{ projectId: string; view: string }>();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();

  // Resolve the theme override from query params (falls back to context).
  const themeOverride = useMemo(() => {
    const t = searchParams.get("theme");
    if (t === "light" || t === "dark") return t;
    return theme;
  }, [searchParams, theme]);

  // Persist embed token on mount, clear on unmount.
  useEffect(() => {
    initEmbedToken();
    return () => clearEmbedToken();
  }, []);

  if (!projectId || !view || !VALID_VIEWS.has(view)) {
    return (
      <Theme appearance={themeOverride} accentColor="blue" radius="medium">
        <Box p="6" style={{ textAlign: "center" }}>
          <Text size="4" color="red">
            Invalid embed URL. Expected /embed/:projectId/:view where view is one of:{" "}
            {[...VALID_VIEWS].join(", ")}
          </Text>
        </Box>
      </Theme>
    );
  }

  return (
    <Theme appearance={themeOverride} accentColor="blue" radius="medium">
      <ProvenanceProvider projectId={projectId}>
        <ViewStateProvider>
          <PlotDataProvider>
            <EmbedShell projectId={projectId} view={view as EmbedView} />
          </PlotDataProvider>
        </ViewStateProvider>
      </ProvenanceProvider>
    </Theme>
  );
}
